import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import Chip from '@material-ui/core/Chip';
import Box from '@material-ui/core/Box';
import Tabs from "@material-ui/core/Tabs";
import Tab from "@material-ui/core/Tab";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import Tooltip from "@material-ui/core/Tooltip";

import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import QuickMessageDialog from "../../components/QuickMessageDialog";
import ConfirmationModal from "../../components/ConfirmationModal";
import toastError from "../../errors/toastError";
import { Grid } from "@material-ui/core";
import { isArray } from "lodash";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_QUICKMESSAGES") {
    const quickmessages = action.payload;
    const newQuickmessages = [];

    if (isArray(quickmessages)) {
      quickmessages.forEach((quickemessage) => {
        const quickemessageIndex = state.findIndex(
          (u) => u.id === quickemessage.id
        );
        if (quickemessageIndex !== -1) {
          state[quickemessageIndex] = quickemessage;
        } else {
          newQuickmessages.push(quickemessage);
        }
      });
    }

    return [...state, ...newQuickmessages];
  }

  if (action.type === "UPDATE_QUICKMESSAGES") {
    const quickemessage = action.payload;
    const quickemessageIndex = state.findIndex((u) => u.id === quickemessage.id);

    if (quickemessageIndex !== -1) {
      state[quickemessageIndex] = quickemessage;
      return [...state];
    } else {
      return [quickemessage, ...state];
    }
  }

  if (action.type === "DELETE_QUICKMESSAGE") {
    const quickemessageId = action.payload;

    const quickemessageIndex = state.findIndex((u) => u.id === quickemessageId);
    if (quickemessageIndex !== -1) {
      state.splice(quickemessageIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
  mediaChip: {
    fontSize: '0.75rem',
    height: 24
  },
  farewellPaper: {
    flex: 1,
    padding: theme.spacing(2),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  farewellListItem: {
    borderRadius: 8,
    marginBottom: 4,
    border: "1px solid transparent",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  farewellShortcode: {
    fontWeight: 700,
    fontSize: "0.85rem",
    color: "#00a884",
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  farewellMessage: {
    fontSize: "0.82rem",
    color: theme.palette.text.secondary,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  farewellEditArea: {
    marginTop: 16,
    padding: 16,
    border: "1px solid #ddd",
    borderRadius: 8,
    backgroundColor: theme.palette.background.default,
  },
  farewellEmptyText: {
    textAlign: "center",
    padding: 24,
    color: theme.palette.text.secondary,
  },
}));

const Quickemessages = () => {
  const classes = useStyles();

  // ── Tab state ──────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState(0);

  // ── Tab 0: Respostas Rápidas (original, untouched) ─────────
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedQuickemessage, setSelectedQuickemessage] = useState(null);
  const [deletingQuickemessage, setDeletingQuickemessage] = useState(null);
  const [quickemessageModalOpen, setQuickMessageDialogOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [quickemessages, dispatch] = useReducer(reducer, []);
  const { user, socket } = useContext(AuthContext);

  const { profile } = user;

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      fetchQuickemessages();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = user.companyId;

    const onQuickMessageEvent = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_QUICKMESSAGES", payload: data.record });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_QUICKMESSAGE", payload: +data.id });
      }
    };
    socket.on(`company-${companyId}-quickemessage`, onQuickMessageEvent);

    return () => {
      socket.off(`company-${companyId}-quickemessage`, onQuickMessageEvent);
    };
  }, [socket]);

  const fetchQuickemessages = async () => {
    try {
      const { data } = await api.get("/quick-messages", {
        params: { searchParam, pageNumber },
      });

      dispatch({ type: "LOAD_QUICKMESSAGES", payload: data.records });
      setHasMore(data.hasMore);
      setLoading(false);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(true);
  };

  const handleCloseQuickMessageDialog = () => {
    setSelectedQuickemessage(null);
    setQuickMessageDialogOpen(false);
    fetchQuickemessages();
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditQuickemessage = (quickemessage) => {
    setSelectedQuickemessage(quickemessage);
    setQuickMessageDialogOpen(true);
  };

  const handleDeleteQuickemessage = async (quickemessageId) => {
    try {
      await api.delete(`/quick-messages/${quickemessageId}`);
      toast.success(i18n.t("quickemessages.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingQuickemessage(null);
    setSearchParam("");
    setPageNumber(1);
    fetchQuickemessages();
    dispatch({ type: "RESET" });
  };

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const getMediaTypeDisplay = (quickmessage) => {
    if (!quickmessage.mediaName) {
      return i18n.t("quickMessages.noAttachment");
    }

    const mediaType = quickmessage.mediaType || 'document';
    const getIcon = (type) => {
      switch (type) {
        case 'audio': return '🎵';
        case 'image': return '🖼️';
        case 'video': return '🎥';
        default: return '📎';
      }
    };

    const getColor = (type) => {
      switch (type) {
        case 'audio': return 'secondary';
        case 'image': return 'primary';
        case 'video': return 'default';
        default: return 'default';
      }
    };

    return (
      <Box display="flex" alignItems="center" gap={1}>
        <span>{getIcon(mediaType)}</span>
        <Chip
          size="small"
          label={mediaType}
          color={getColor(mediaType)}
          className={classes.mediaChip}
        />
      </Box>
    );
  };

  // ── Tab 1: Mensagens de Encerramento ───────────────────────
  const [farewellMessages, setFarewellMessages] = useState([]);
  const [farewellLoading, setFarewellLoading] = useState(false);
  const [farewellEditing, setFarewellEditing] = useState(null);
  const [farewellSaving, setFarewellSaving] = useState(false);
  const [farewellDeletingId, setFarewellDeletingId] = useState(null);

  const fetchFarewellMessages = async () => {
    setFarewellLoading(true);
    try {
      const { data } = await api.get("/quick-messages/farewell");
      setFarewellMessages(data || []);
    } catch (err) {
      toastError(err);
    } finally {
      setFarewellLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 1) {
      fetchFarewellMessages();
    }
  }, [activeTab]);

  const handleFarewellStartNew = () => {
    setFarewellEditing({ id: "new", shortcode: "", message: "" });
  };

  const handleFarewellStartEdit = (msg) => {
    setFarewellEditing({ id: msg.id, shortcode: msg.shortcode, message: msg.message });
  };

  const handleFarewellCancelEdit = () => {
    setFarewellEditing(null);
  };

  const handleFarewellSave = async () => {
    if (!farewellEditing.shortcode.trim() || !farewellEditing.message.trim()) {
      toast.warning("Preencha o título e a mensagem.");
      return;
    }
    setFarewellSaving(true);
    try {
      if (farewellEditing.id === "new") {
        await api.post("/quick-messages", {
          shortcode: farewellEditing.shortcode.trim(),
          message: farewellEditing.message.trim(),
          category: "farewell",
        });
      } else {
        await api.put(`/quick-messages/${farewellEditing.id}`, {
          shortcode: farewellEditing.shortcode.trim(),
          message: farewellEditing.message.trim(),
          category: "farewell",
        });
      }
      toast.success("Mensagem salva!");
      setFarewellEditing(null);
      await fetchFarewellMessages();
    } catch (err) {
      toastError(err);
    } finally {
      setFarewellSaving(false);
    }
  };

  const handleFarewellDelete = async (id) => {
    setFarewellDeletingId(id);
    try {
      await api.delete(`/quick-messages/${id}`);
      setFarewellMessages((prev) => prev.filter((m) => m.id !== id));
      toast.success("Mensagem removida.");
    } catch (err) {
      toastError(err);
    } finally {
      setFarewellDeletingId(null);
    }
  };

  // ── Render ─────────────────────────────────────────────────
  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingQuickemessage && `${i18n.t("quickMessages.confirmationModal.deleteTitle")} ${deletingQuickemessage.shortcode}?`}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteQuickemessage(deletingQuickemessage.id)}
      >
        {i18n.t("quickMessages.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <QuickMessageDialog
        resetPagination={() => {
          setPageNumber(1);
          fetchQuickemessages();
        }}
        open={quickemessageModalOpen}
        onClose={handleCloseQuickMessageDialog}
        aria-labelledby="form-dialog-title"
        quickemessageId={selectedQuickemessage && selectedQuickemessage.id}
      />
      <MainHeader>
        <Grid style={{ width: "99.6%" }} container>
          <Grid xs={12} sm={8} item>
            <Title>{i18n.t("quickMessages.title")}</Title>
          </Grid>
          {activeTab === 0 && (
            <Grid xs={12} sm={4} item>
              <Grid spacing={2} container>
                <Grid xs={6} sm={6} item>
                  <TextField
                    fullWidth
                    placeholder={i18n.t("quickMessages.searchPlaceholder")}
                    type="search"
                    value={searchParam}
                    onChange={handleSearch}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: "gray" }} />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
                <Grid xs={6} sm={6} item>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleOpenQuickMessageDialog}
                    color="primary"
                  >
                    {i18n.t("quickMessages.buttons.add")}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          )}
        </Grid>
      </MainHeader>

      <Tabs
        value={activeTab}
        onChange={(e, v) => setActiveTab(v)}
        indicatorColor="primary"
        textColor="primary"
        style={{ borderBottom: "1px solid #e0e0e0", marginBottom: 8 }}
      >
        <Tab label="Respostas Rápidas" />
        <Tab label="Mensagens de Encerramento" />
      </Tabs>

      {activeTab === 0 && (
        <Paper
          className={classes.mainPaper}
          variant="outlined"
          onScroll={handleScroll}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="center">
                  {i18n.t("quickMessages.table.shortcode")}
                </TableCell>
                <TableCell align="center">
                  Mídia
                </TableCell>
                <TableCell align="center">
                  {i18n.t("quickMessages.table.status")}
                </TableCell>
                <TableCell align="center">
                  {i18n.t("quickMessages.table.actions")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <>
                {quickemessages.map((quickemessage) => (
                  <TableRow key={quickemessage.id}>
                    <TableCell align="center">{quickemessage.shortcode}</TableCell>
                    <TableCell align="center">
                      {getMediaTypeDisplay(quickemessage)}
                    </TableCell>
                    <TableCell align="center">
                      {quickemessage.geral === true ? (
                        <CheckCircleIcon style={{ color: 'green' }} />
                      ) : (
                        ''
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleEditQuickemessage(quickemessage)}
                      >
                        <EditIcon />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(e) => {
                          setConfirmModalOpen(true);
                          setDeletingQuickemessage(quickemessage);
                        }}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {loading && <TableRowSkeleton columns={4} />}
              </>
            </TableBody>
          </Table>
        </Paper>
      )}

      {activeTab === 1 && (
        <Paper className={classes.farewellPaper} variant="outlined">
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
              Mensagens de Encerramento
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleFarewellStartNew}
              disabled={!!farewellEditing || farewellLoading}
              style={{ color: "#00a884", borderColor: "#00a884" }}
            >
              Nova mensagem
            </Button>
          </Box>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
            Estas mensagens ficam disponíveis no botão "Resolver" para enviar ao encerrar um atendimento.
          </Typography>

          {farewellLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress size={28} style={{ color: "#00a884" }} />
            </Box>
          ) : (
            <>
              {farewellMessages.length === 0 && !farewellEditing && (
                <Typography className={classes.farewellEmptyText}>
                  Nenhuma mensagem de encerramento cadastrada.
                  <br />
                  Clique em "+ Nova mensagem" para adicionar.
                </Typography>
              )}
              <List disablePadding>
                {farewellMessages.map((msg) => (
                  <React.Fragment key={msg.id}>
                    <ListItem className={classes.farewellListItem} alignItems="flex-start">
                      <ListItemText
                        primary={
                          <span className={classes.farewellShortcode}>{msg.shortcode}</span>
                        }
                        secondary={
                          <span className={classes.farewellMessage}>
                            {msg.message.length > 150
                              ? msg.message.substring(0, 150) + "\u2026"
                              : msg.message}
                          </span>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => handleFarewellStartEdit(msg)}
                            disabled={!!farewellEditing}
                            style={{ marginRight: 2 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Excluir">
                          <IconButton
                            size="small"
                            onClick={() => handleFarewellDelete(msg.id)}
                            disabled={farewellDeletingId === msg.id || !!farewellEditing}
                          >
                            {farewellDeletingId === msg.id ? (
                              <CircularProgress size={16} />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))}
              </List>

              {farewellEditing && (
                <div className={classes.farewellEditArea} onKeyDown={(e) => e.stopPropagation()}>
                  <Typography
                    variant="caption"
                    style={{ fontWeight: 700, marginBottom: 8, display: "block" }}
                  >
                    {farewellEditing.id === "new" ? "Nova mensagem de encerramento" : "Editar mensagem de encerramento"}
                  </Typography>
                  <TextField
                    label="Título (ex: Padrão, Inatividade, Resolvido)"
                    value={farewellEditing.shortcode}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFarewellEditing((prev) => ({ ...prev, shortcode: val }));
                    }}
                    fullWidth
                    size="small"
                    variant="outlined"
                    style={{ marginBottom: 12 }}
                    inputProps={{ maxLength: 50 }}
                  />
                  <TextField
                    label="Texto da mensagem"
                    value={farewellEditing.message}
                    onChange={(e) => {
                      const val = e.target.value;
                      setFarewellEditing((prev) => ({ ...prev, message: val }));
                    }}
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={10}
                    size="small"
                    variant="outlined"
                    helperText="Use Enter para parágrafos. Você pode usar variáveis como {{name}}."
                  />
                  <Box display="flex" justifyContent="flex-end" style={{ gap: 8, marginTop: 12 }}>
                    <Button size="small" onClick={handleFarewellCancelEdit} disabled={farewellSaving}>
                      Cancelar
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={handleFarewellSave}
                      disabled={farewellSaving}
                      startIcon={farewellSaving ? <CircularProgress size={14} /> : <CheckIcon />}
                      style={{ backgroundColor: "#00a884", color: "#fff" }}
                    >
                      Salvar
                    </Button>
                  </Box>
                </div>
              )}
            </>
          )}
        </Paper>
      )}
    </MainContainer>
  );
};

export default Quickemessages;
